import * as React from "react";

import { Header, InterfaceHeaderLinks, ThemeProvider, Footer, Sidebar } from "../";
import { useTranslation } from "react-i18next";
import "./Layout.scss";
import "../../styles/fonts.scss";

import "../../i18n/i18n";
import { CloseIcon, ListIcon } from "@tiendanube/icons";
import classNames from "classnames";

export interface InterfaceLayout {
  children: React.ReactNode;
  headerLinks: InterfaceHeaderLinks[];
  headerLogoOnClick: () => void;
  sidebar?: React.ReactNode;
}

function Layout({
  children,
  headerLinks,
  headerLogoOnClick,
  sidebar,
}: InterfaceLayout): JSX.Element {
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => { 
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => 
      window.removeEventListener("resize",updateDimensions);
  }, [])

  const updateDimensions = () => {
    const width = window.innerWidth
    setIsMobile(width < 672)
  }

  const { t } = useTranslation();

  const [selected, toggleSelected] = React.useState(false);

  function handleToggle() {
    toggleSelected(!selected);
  }
  
  const sidebarOffset = isMobile ? 0 : 220;
  const sidebarWidth = 220;

  const hasSidebar = (
    <Sidebar style={{width: sidebarWidth}}>
      {sidebar}
    </Sidebar>
  );

  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  function handleSidebar() {
    setSidebarOpen(!sidebarOpen);
  }

  const sidebarMobileClassName = classNames("nimbus-web--sidebar--mobile",{"open": sidebarOpen});
  
  return (
    <ThemeProvider theme={selected}>
      <div className="nimbus-web--layout">
        {sidebar && (
          isMobile ?
            <>
              <div className={sidebarMobileClassName}>
                {hasSidebar}
              </div>
              <button onClick={handleSidebar} type="button" className="nimbus-web--button--floating" aria-label={t("layout.button.sidebar")}>
                {sidebarOpen ?
                  <CloseIcon size="medium" />
                :
                  <ListIcon size="medium" />
                }
              </button>
            </>
          :
            hasSidebar
        )}
        <div className="nimbus-web--main" style={sidebar && { paddingLeft: sidebarOffset }}>
          <Header
            onClickToggle={handleToggle}
            toggleState={selected}
            onClickLogo={headerLogoOnClick}
            links={headerLinks}
            style={sidebar && { paddingLeft: sidebarOffset }}
          />
          <div className="nimbus-web--body">
            {children}
          </div>
          <Footer
            firstColumnLinks={[
              {
                text: `${t("footer.links.design.text")}`,
                href: `${t("footer.links.design.url")}`,
              },
              {
                text: `${t("footer.links.engineering.text")}`,
                href: `${t("footer.links.engineering.url")}`,
              }
            ]}
            secondColumnLinks={[
              {
                text: `${t("footer.links.partners.text")}`,
                href: `${t("footer.links.partners.url")}`,
              },
              {
                text: `${t("footer.links.documentation.text")}`,
                href: `${t("footer.links.documentation.url")}`,
              }
            ]}
            socialMediaLinks={[
              {
                label: "Instagram",
                href: `${t("footer.social.links.instagram")}`,
                icon: "instagram"
              },
              {
                label: "Facebook",
                href: `${t("footer.social.links.facebook")}`,
                icon: "facebook"
              },
              {
                label: "Twitter",
                href: `${t("footer.social.links.twitter")}`,
                icon: "twitter"
              },
              {
                label: "YouTube",
                href: `${t("footer.social.links.youtube")}`,
                icon: "youtube"
              },
              {
                label: "LinkedIn",
                href: `${t("footer.social.links.linkedin")}`,
                icon: "linkedin"
              },
              {
                label: "Github",
                href: `${t("footer.social.links.github")}`,
                icon: "github"
              },
            ]}
            legalText={`${t("footer.legal.copyright")}`}
            legalLinks={[
              {
                text: `${t("footer.legal.links.terms.cta")}`,
                href: `${t("footer.legal.links.terms.ctaUrl")}`
              },
              {
                text: `${t("footer.legal.links.privacy.cta")}`,
                href: `${t("footer.legal.links.privacy.ctaUrl")}`
              },
              {
                text: `${t("footer.legal.links.quality.cta")}`,
                href: `${t("footer.legal.links.quality.ctaUrl")}`
              }
            ]}
          />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default Layout;
